import { useParams } from 'react-router-dom';
import { useLocale } from '@/contexts/LocaleContext';
import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { API } from '@/lib/api';
import { toast } from 'sonner';
import { Send, Phone, MessageCircle, Video, MapPin, Mail } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { HreflangTags } from '@/components/SEOComponents';
import { analytics, getStoredUTM } from '@/lib/analytics';

export default function ContactPage() {
  const { locale } = useParams();
  const { t } = useLocale();
  const loc = locale || 'en';
  const [form, setForm] = useState({ name: '', email: '', phone: '', country: '', program_interest: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [contactInfo, setContactInfo] = useState(null);
  const [programs, setPrograms] = useState([]);

  useEffect(() => {
    fetch(`${API}/api/public/site-content/contact_info?locale=${loc}`)
      .then(r => r.json())
      .then(data => setContactInfo(data.content || {}))
      .catch(() => {});
    api.get(`/programs?locale=${loc}`)
      .then(res => setPrograms(res.data.programs || []))
      .catch(() => {});
  }, [loc]);

  const ci = (field, fallback) => contactInfo?.[field] || fallback || '';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const utm = getStoredUTM();
      await api.post('/leads/capture', {
        ...form,
        source: 'contact_page',
        capture_type: 'request_info',
        locale: loc,
        page_url: window.location.href,
        ...utm,
      });
      setSubmitted(true);
      toast.success(t('contact.success_toast'));
      analytics.leadFormSubmit('contact', loc, form.program_interest);
    } catch {
      toast.error(t('contact.error_toast'));
      analytics.formSubmitError('contact', 'submission_failed', loc);
    }
    setSubmitting(false);
  };

  const CTA_CHANNELS = [
    { icon: Phone, label: 'WhatsApp', href: ci('whatsapp_url', 'https://wa.me/302113005000'), color: 'bg-green-500/10 text-green-600' },
    { icon: MessageCircle, label: 'Messenger', href: ci('messenger_url', 'https://m.me/miselgroup'), color: 'bg-blue-500/10 text-blue-600' },
    { icon: Phone, label: 'Viber', href: ci('viber_url', 'viber://chat?number=+302113005000'), color: 'bg-purple-500/10 text-purple-600' },
    { icon: Video, label: 'Google Meet', href: ci('google_meet_url', '#'), color: 'bg-red-500/10 text-red-600' },
  ].filter(ch => ch.href && ch.href !== '#');

  return (
    <div data-testid="contact-page">
      <Helmet><title>{`${t('nav.contact')} — MISEL GROUP MASTERCLASS`}</title></Helmet>
      <HreflangTags path="/contact" />
      <section className="relative min-h-[50vh] flex items-end pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-foreground/[0.03]" />
        <div className="relative z-10 max-w-[1600px] mx-auto px-6 md:px-12 w-full pt-32">
          <p className="caption-text animate-fade-up text-gold mb-4">{t('nav.contact')}</p>
          <h1 className="heading-xl animate-fade-up delay-100 max-w-4xl">{t('contact.hero_title')}</h1>
          <p className="font-accent italic text-xl text-muted-foreground mt-4 animate-fade-up delay-200">{t('contact.hero_subtitle')}</p>
        </div>
      </section>

      <section className="section-padding-sm">
        <div className="max-w-[1600px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16">
          <div className="animate-fade-up">
            <h2 className="heading-md mb-8">{t('contact.form_title')}</h2>
            {submitted ? (
              <div className="card-glass p-12 text-center" data-testid="contact-success">
                <div className="text-gold text-5xl mb-4">&#10003;</div>
                <h3 className="heading-sm mb-2">{t('contact.success_title')}</h3>
                <p className="text-sm text-muted-foreground">{t('contact.success_desc')}</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="contact-form">
                <input className="input-glass w-full" placeholder={t('contact.first_name')} required value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} data-testid="contact-name" />
                <input className="input-glass w-full" type="email" placeholder={t('contact.email')} required value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} data-testid="contact-email" />
                <input className="input-glass w-full" placeholder={t('contact.phone')} value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} data-testid="contact-phone" />
                <input className="input-glass w-full" placeholder={t('contact.country')} value={form.country} onChange={e => setForm(p => ({ ...p, country: e.target.value }))} data-testid="contact-country" />
                <select className="input-glass w-full text-muted-foreground" value={form.program_interest} onChange={e => setForm(p => ({ ...p, program_interest: e.target.value }))} data-testid="contact-program">
                  <option value="">{t('contact.program_interest')}</option>
                  {programs.map(p => (
                    <option key={p.id || p.slug} value={p.title}>{p.title}</option>
                  ))}
                </select>
                <textarea className="input-glass w-full min-h-[120px]" placeholder={t('contact.message')} value={form.message} onChange={e => setForm(p => ({ ...p, message: e.target.value }))} data-testid="contact-message" />
                <button type="submit" className="btn-primary w-full" disabled={submitting} data-testid="contact-submit">
                  {submitting ? t('contact.sending') : t('contact.send')} <Send size={16} className="ml-2" strokeWidth={1.5} />
                </button>
              </form>
            )}
          </div>

          <div className="animate-fade-up delay-200">
            <h2 className="heading-md mb-8">{t('contact.direct_title')}</h2>
            {CTA_CHANNELS.length > 0 && (
              <div className="space-y-4 mb-12">
                {CTA_CHANNELS.map((ch, i) => {
                  const Icon = ch.icon;
                  return (
                    <a key={i} href={ch.href} target="_blank" rel="noopener noreferrer" className="card-glass flex items-center gap-4 p-5" data-testid={`cta-${ch.label.toLowerCase()}`}>
                      <div className={`w-12 h-12 rounded-xl ${ch.color} flex items-center justify-center`}><Icon size={20} strokeWidth={1.5} /></div>
                      <div>
                        <p className="font-medium text-sm">{ch.label}</p>
                        <p className="text-xs text-muted-foreground">{t('contact.chat_admissions')}</p>
                      </div>
                    </a>
                  );
                })}
              </div>
            )}

            {/* Contact Info Card */}
            <div className="card-glass p-8 space-y-4" data-testid="contact-info-card">
              <MapPin size={20} className="text-gold" strokeWidth={1.5} />
              <div>
                <p className="text-xs text-gold uppercase tracking-wider font-medium mb-2">{t('contact.visit_title')}</p>
                <p className="text-sm font-semibold">{ci('company_name', 'MISEL GROUP MASTERCLASS')}</p>
                <p className="text-xs text-muted-foreground mt-1 uppercase tracking-wide">
                  {ci('school_type', '')}
                </p>
              </div>
              <div className="flex items-start gap-3">
                <MapPin size={14} className="text-muted-foreground mt-0.5 flex-shrink-0" />
                <p className="text-sm text-muted-foreground">
                  {ci('address', '')}<br />
                  {ci('city', '')}
                </p>
              </div>
              {ci('email') && (
                <div className="flex items-center gap-3">
                  <Mail size={14} className="text-muted-foreground flex-shrink-0" />
                  <a href={`mailto:${ci('email')}`} className="text-sm text-muted-foreground hover:text-foreground transition-colors">{ci('email')}</a>
                </div>
              )}
              {ci('phone') && (
                <div className="flex items-center gap-3">
                  <Phone size={14} className="text-muted-foreground flex-shrink-0" />
                  <a href={`tel:${ci('phone').replace(/\s/g, '')}`} className="text-sm text-muted-foreground hover:text-foreground transition-colors">{ci('phone')}</a>
                </div>
              )}
            </div>

            {/* Google Map */}
            <div className="mt-6 rounded-2xl overflow-hidden border border-border/50" data-testid="google-map">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3145.5!2d23.6777!3d37.9922!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x14a1bb5d7f8e5f05%3A0x0!2zSWVyYSBPZG9zIDIzNiwgQWlnYWxlbyAxMjI0Mg!5e0!3m2!1sen!2sgr!4v1700000000000"
                width="100%"
                height="300"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="MISEL GROUP MASTERCLASS Location"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
